import { useCallback } from "react";
import useSWRImmutable from "swr/immutable";
import useSWRMutation from "swr/mutation";
import { Paginated, PaginationQuery, UpdateOrderRequestBody, UpdateVisibilityRequestBody } from "../../types";
import { buildUrl, mutateCache, useApi } from "../api";
import { CustomError } from "../errors";
import { InsertRailCapBody, RailCap, UpdateRailCapBody } from "./types/railCaps";

export const useRailCapsApi = () => {
  const api = useApi();

  const getAdminRailCap = useCallback(
    (railCapId: string, signal: AbortSignal) => {
      const url = `/admin/settings/border/rail_caps/${railCapId}`;
      return useSWRMutation<RailCap, CustomError, string>(url, (url) => api.get<RailCap>({ url, signal }));
    },
    [api],
  );

  const useAdminRailCaps = useCallback(
    (query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl(`/admin/settings/border/rail_caps`, query);
      return useSWRImmutable<Paginated<RailCap>, CustomError, string>(url, (url) =>
        api.get<Paginated<RailCap>>({ url, signal }),
      );
    },
    [api],
  );

  const getAdminRailCaps = useCallback(
    (query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl(`/admin/settings/border/rail_caps`, query);
      return api.get<Paginated<RailCap>>({ url, signal }).then((x) => x.items);
    },
    [api],
  );

  const insertAdminRailCap = useCallback(
    async (body: InsertRailCapBody): Promise<{ id: string }> => {
      const url = `/admin/settings/border/rail_caps`;
      const id = await api.post<{ id: string }, InsertRailCapBody>(url, body);
      mutateCache([url, "/admin/settings/border/rails"]);
      return id;
    },
    [api],
  );

  const updateAdminRailCap = useCallback(
    async (railCapId: string, body: UpdateRailCapBody): Promise<void> => {
      const url = `/admin/settings/border/rail_caps/${railCapId}`;
      await api.put<UpdateRailCapBody>(url, body);
      mutateCache(["/admin/settings/border/rail_caps", "/admin/settings/border/rails"]);
    },
    [api],
  );

  const updateAdminRailCapVisibility = useCallback(
    async (railCapId: string, visibility: UpdateVisibilityRequestBody): Promise<void> => {
      const url = `/admin/settings/border/rail_caps/${railCapId}/visibility`;
      (await api.put<UpdateVisibilityRequestBody>(url, visibility),
        mutateCache(["/admin/settings/border/rail_caps", "/admin/settings/border/rails"]));
    },
    [api],
  );

  const updateAdminRailCapOrder = useCallback(
    async (railCapId: string, order: UpdateOrderRequestBody): Promise<void> => {
      const url = `/admin/settings/border/rail_caps/${railCapId}/order`;
      (await api.put<UpdateOrderRequestBody>(url, order),
        mutateCache(["/admin/settings/border/rail_caps", "/admin/settings/border/rails"]));
    },
    [api],
  );

  const deleteAdminRailCap = useCallback(
    async (railCap: RailCap): Promise<void> => {
      const url = `/admin/settings/border/rail_caps/${railCap.item.id}`;
      await api.del(url);
      mutateCache([`/admin/settings/border/rail_caps`, "/admin/settings/border/rails"]);
    },
    [api],
  );

  return {
    getAdminRailCap,
    insertAdminRailCap,
    updateAdminRailCap,
    updateAdminRailCapVisibility,
    updateAdminRailCapOrder,
    deleteAdminRailCap,
    useAdminRailCaps,
    getAdminRailCaps,
  };
};
